import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Globe, MapPin, PhoneCall, CheckCircle2 } from "lucide-react";
import { api, telHref } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CategoryIcon } from "../components/CategoryIcon";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function OrgDetail() {
  const { id } = useParams();
  const { t, catLabel } = useLang();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    api.get(`/organizations/${id}`).then((r) => setOrg(r.data)).catch(() => setOrg(false));
  }, [id]);

  if (org === null) {
    return <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12"><Skeleton className="h-96 rounded-3xl" /></div>;
  }
  if (org === false) {
    return <div className="max-w-4xl mx-auto px-6 py-24 text-center text-stone-500">Organisation not found.</div>;
  }

  const c = org.contact || {};
  const ContactRow = ({ icon: Icon, label, value, href, testid }) =>
    value ? (
      <a href={href} data-testid={testid} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
        className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F3F0] transition-colors group">
        <span className="w-10 h-10 rounded-xl bg-[#E8EFE9] text-[#2E604A] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <span className="min-w-0">
          <span className="block text-xs font-bold uppercase tracking-wider text-stone-400">{label}</span>
          <span className="block text-sm font-semibold text-stone-800 truncate group-hover:text-[#2E604A]">{value}</span>
        </span>
      </a>
    ) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 fade-up">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-[#2E604A] mb-6">
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}
      </Link>

      <div className="flex flex-wrap gap-2 mb-4">
        {(org.categories || []).map((cat) => (
          <Badge key={cat} className="rounded-full font-semibold border border-stone-300 bg-stone-100 text-stone-900">
            <CategoryIcon category={cat} className="w-3.5 h-3.5 mr-1" /> {catLabel(cat)}
          </Badge>
        ))}
      </div>

      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">{org.name}</h1>
      <p className="mt-4 text-base text-stone-700 leading-relaxed">{org.short_description}</p>

      <div className="grid md:grid-cols-5 gap-6 mt-8">
        <div className="md:col-span-3">
          <h2 className="font-display text-xl font-bold text-stone-900 mb-4">{t("services")}</h2>
          <ul className="space-y-3">
            {(org.services || []).map((s, i) => (
              <li key={`${i}-${s}`} className="flex items-start gap-3 text-sm text-stone-700">
                <CheckCircle2 className="w-5 h-5 text-[#2E604A] shrink-0 mt-0.5" />
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4 sticky top-24">
            <h2 className="font-display text-lg font-bold text-stone-900 px-3 mb-1">{t("contact")}</h2>
            <ContactRow icon={PhoneCall} label={t("call")} value={c.freephone} href={`tel:${telHref(c.freephone)}`} testid="contact-freephone" />
            <ContactRow icon={Phone} label={t("call")} value={c.phone} href={`tel:${telHref(c.phone)}`} testid="contact-phone" />
            <ContactRow icon={Mail} label={t("email")} value={c.email} href={`mailto:${c.email}`} testid="contact-email" />
            <ContactRow icon={MapPin} label={t("f_address")} value={c.address} href={c.address ? `https://maps.google.com/?q=${encodeURIComponent(c.address)}` : null} testid="contact-address" />
            {c.website && (
              <a href={c.website} target="_blank" rel="noreferrer" data-testid="contact-website" className="block mt-3">
                <Button className="w-full rounded-full bg-[#2E604A] hover:bg-[#234B3A] gap-2 h-11">
                  <Globe className="w-4 h-4" /> {t("visit_website")}
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
